import { inquiryHref } from '@/lib/engagements';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FAQ, { type FAQItem } from '@/components/FAQ';
import Callout from '@/components/Callout';
import { Choice, Panel, ReadingList, ToolIntro, ToolSection } from '@/components/ToolKit';
import { downloadJson } from '@/lib/tools/download';

// Browser-only self-assessment. No environment inspection is performed.

type Level = 'documented' | 'partly' | 'unknown';
const LEVELS: { value: Level; label: string }[] = [
  { value: 'documented', label: 'Documented and current' },
  { value: 'partly', label: 'Partly' },
  { value: 'unknown', label: 'Unknown' },
];
const SCORE: Record<Level, number> = { documented: 2, partly: 1, unknown: 0 };

interface Dimension {
  id: string;
  title: string;
  question: string;
  reports: string;
}

const DIMENSIONS: Dimension[] = [
  {
    id: 'components',
    title: 'Installed components',
    question: 'Is there a current list of every installed component, with versions, dependencies and who owns each?',
    reports: 'Collect the component inventory, exact versions, manifests, dependencies and accountable owners.',
  },
  {
    id: 'workflows',
    title: 'Workflow definitions',
    question: 'Are the workflow definitions inventoried, with unused ones identified and the approval routing understood?',
    reports: 'Identify the deployed workflows, observed usage, task owners, integrations and failure-handling paths.',
  },
  {
    id: 'storage',
    title: 'Storage and archive',
    question: 'Do you know storage use by file system, database and object storage, growth by content type, and which content could be retired?',
    reports: 'Document configured storage providers, content locations, growth evidence, retention instructions and ERP document links.',
  },
  {
    id: 'security',
    title: 'Security model',
    question: 'Are the custom security groups, roles and account hierarchy documented, and has the access pattern been reviewed for the last audit?',
    reports: 'Collect roles, security groups, accounts and access rules; agree positive and negative retrieval tests with the security owner.',
  },
  {
    id: 'customizations',
    title: 'Customizations',
    question: 'Are ADF customizations, custom content profiles, custom services, validation rules and search-result templates all known and written down?',
    reports: 'Identify custom forms, profiles, services, validation rules and templates, including their source and runtime dependencies.',
  },
  {
    id: 'path',
    title: 'Forward-path mapping',
    question: 'Has each finding been mapped to a forward path (move to OCI as-is, upgrade to 14c, re-platform, hybrid) with a sequence and an owner?',
    reports: 'Connect verified findings to applicable options, missing evidence, proposed owners and acceptance gates.',
  },
];

interface Reading {
  total: number;
  headline: string;
  body: string;
  startWith: Dimension[];
}

const readAnswers = (a: Record<string, Level | null>): Reading | null => {
  if (DIMENSIONS.some((d) => a[d.id] === null)) return null;
  const total = DIMENSIONS.reduce((s, d) => s + SCORE[a[d.id] as Level], 0);
  const unknown = DIMENSIONS.filter((d) => a[d.id] === 'unknown');
  const partly = DIMENSIONS.filter((d) => a[d.id] === 'partly');
  let headline: string;
  let body: string;
  if (unknown.length) {
    headline = 'Resolve the unknown dimensions';
    body = 'A high total cannot close an unknown dependency. Start with the gaps below and verify the supporting evidence before selecting an implementation path.';
  } else if (total === 12) {
    headline = 'Well documented';
    body = 'You report current documentation across all six areas. Validate its completeness and accuracy; these answers do not prove system health, upgrade compatibility or readiness to change.';
  } else if (total >= 6) {
    headline = 'Partly documented';
    body = 'Enough is known to start the conversation, not enough to commit to a path. Start discovery with the dimensions listed, then choose.';
  } else {
    headline = 'Largely undocumented';
    body = 'Run a full discovery before choosing a path. Deciding on partial knowledge is how upgrade programs find their surprises in the test phase.';
  }
  return { total, headline, body, startWith: [...unknown, ...partly] };
};

const FAQS: FAQItem[] = [
  { q: 'Does this inspect my environment?', a: 'No. This page summarizes six answers you supply. It does not connect to WebCenter, read configuration or certify readiness.' },
  { q: 'Can I request a deeper inspection?', a: 'Yes. An Estate Review can scope the evidence, access and collection method for your exact releases. Any installed utility needs a separate package, permissions, compatibility, network-behavior and removal review before use; this page does not offer a verified installation package.' },
  { q: 'What does the score mean?', a: 'Documented answers receive two points, partly documented answers one, and unknown answers zero. It is an organizational aid based on self-report. A high total cannot resolve an unknown security, recovery or integration dependency.' },
  { q: 'Are my answers sent anywhere?', a: 'The page calculates locally. Reset clears the answers, and Download summary saves a JSON file only when you choose it. The answers are not attached to an inquiry.' },
];

const WebCenterContentInspector = () => {
  const [answers, setAnswers] = useState<Record<string, Level | null>>(() =>
    Object.fromEntries(DIMENSIONS.map((d) => [d.id, null])),
  );
  const reading = useMemo(() => readAnswers(answers), [answers]);
  const answered = DIMENSIONS.filter((d) => answers[d.id] !== null).length;

  return (
    <>
      <ToolIntro>
        <p>
          Use this six-question check to organize the evidence already available about your WebCenter estate.
          It identifies missing or partial documentation from your answers. It does not inspect the system or
          establish that an upgrade, migration or production change is safe.
        </p>
      </ToolIntro>

      <ToolSection id="check" title="Discovery readiness check">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <div className="border-t border-rule">
            {DIMENSIONS.map((d, i) => (
              <Choice
                key={d.id}
                name={d.id}
                legend={
                  <>
                    <span className="label mr-3">{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-bold">{d.title}.</span> {d.question}
                  </>
                }
                value={answers[d.id]}
                options={LEVELS}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [d.id]: v }))}
              />
            ))}
          </div>
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Panel>
              <p className="label">Reading</p>
              {reading ? (
                <div role="status">
                  <p className="mt-3 text-[1.75rem] font-bold leading-[1.2]">{reading.headline}</p>
                  <p className="mt-2 text-small">
                    {reading.total} of {DIMENSIONS.length * 2}
                  </p>
                  <p className="mt-3 text-body">{reading.body}</p>
                  {reading.startWith.length > 0 && (
                    <div className="mt-5 border-t border-rule pt-5">
                      <p className="label">Start discovery with</p>
                      <ol className="mt-3 space-y-3">
                        {reading.startWith.map((d) => (
                          <li key={d.id} className="border-l-[3px] border-l-brass pl-4">
                            <p className="text-small">{d.title}</p>
                            <p className="mt-1 text-[1rem] leading-[1.45]">{d.reports}</p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-body" role="status">
                  Answer all six. {answered} of {DIMENSIONS.length} answered.
                </p>
              )}
            </Panel>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" className="btn-outline-forest" onClick={() => setAnswers(Object.fromEntries(DIMENSIONS.map(d => [d.id, null])))}>Reset answers</button>
          {reading && <button type="button" className="btn-forest" onClick={() => downloadJson('ecmworks-discovery-summary.json', { modelVersion: '2026-09-16.2', basis: 'Self-reported documentation; no environment inspection', answers, result: reading })}>Download summary</button>}
        </div>
      </ToolSection>

      <ToolSection id="reading" title="How to read the result">
        <ReadingList
          items={[
            {
              title: 'The score is a documentation score, not a health score',
              body: 'An estate can be well documented and still need a great deal of work. The reading organizes your answers; it does not measure how much work the estate needs.',
            },
            {
              title: 'Unknown dimensions come first',
              body: 'They are where the surprises live. Components and customizations are usually the two that decide the size of an upgrade; security and storage decide the audit story.',
            },
            {
              title: 'Forward-path mapping is the last dimension for a reason',
              body: 'Use confirmed evidence from the other areas to compare options. An unanswered item remains open regardless of the total score.',
            },
          ]}
        />
        <p className="mt-5 max-w-[46rem] text-body">
          <Link to="/tools/webcenter-decision-guide" className="link">
            The decision guide
          </Link>{' '}
          ·{' '}
          <Link to="/tools/wcc-component-inventory" className="link">
            The component inventory
          </Link>
        </p>
      </ToolSection>

      <ToolSection id="installed" title="A scoped review of the evidence">
        <div className="max-w-[46rem]">
          <p className="text-body">An engineer can help validate the inventory, trace dependencies and agree acceptance evidence. The collection method depends on the releases, available documentation, access controls and the work in scope.</p>
          <ul className="mt-5 border-t border-rule">{DIMENSIONS.map(d => <li key={d.id} className="border-b border-rule py-4"><h3 className="text-small">{d.title}</h3><p className="mt-1 text-body">{d.reports}</p></li>)}</ul>
          <Callout kind="decision"><p>Start with an <Link to="/estate-review">Estate Review</Link> or <Link to={inquiryHref('/tools/webcenter-content-inspector')}>discuss an inspection</Link>. Agree the package, permissions, data handling, compatibility checks and removal process before installing any collection utility. This page makes no claim that a particular installed package has been verified for your environment.</p></Callout>
        </div>
      </ToolSection>

      <div className="mt-16 max-w-[46rem]">
        <FAQ items={FAQS} />
      </div>
    </>
  );
};

export default WebCenterContentInspector;
